import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Video, Square, RotateCcw, Circle, RefreshCw, Mic } from "lucide-react";
import { toast } from "sonner";

// In-app camera or voice recorder using MediaRecorder — outputs compressed webm.
export default function Recorder({ maxDur = 45, audioOnly = false, onDone, onCancel }) {
  const [state, setState] = useState("idle"); // idle | requesting | previewing | recording | done
  const [elapsed, setElapsed] = useState(0);
  const [facing, setFacing] = useState("user");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const attachStream = async (currentFacing) => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    const constraints = audioOnly
      ? { audio: true }
      : { video: { facingMode: { ideal: currentFacing }, width: { ideal: 720 }, height: { ideal: 1280 } }, audio: true };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    streamRef.current = stream;
    if (videoRef.current && !audioOnly) {
      videoRef.current.srcObject = stream;
      try { await videoRef.current.play(); } catch {}
    }
  };

  const requestCamera = async () => {
    setState("requesting");
    try {
      await attachStream(facing);
      setState("previewing");
    } catch (e) {
      toast.error("Camera access denied. Enable camera permissions and try again.");
      setState("idle");
    }
  };

  const flip = async () => {
    if (state !== "previewing") return;
    const next = facing === "user" ? "environment" : "user";
    setFacing(next);
    try { await attachStream(next); }
    catch { toast.error("Camera flip failed"); }
  };

  const start = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const videoPreferred = [
      "video/webm;codecs=vp8,opus",
      "video/webm;codecs=vp9,opus",
      "video/webm",
      "video/mp4",
    ];
    const audioPreferred = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
    ];
    const preferred = audioOnly ? audioPreferred : videoPreferred;
    const mime = preferred.find((m) => (window.MediaRecorder?.isTypeSupported ? MediaRecorder.isTypeSupported(m) : false)) || "";
    try {
      const opts = audioOnly
        ? { mimeType: mime || undefined, audioBitsPerSecond: 96_000 }
        : { mimeType: mime || undefined, videoBitsPerSecond: 1_200_000, audioBitsPerSecond: 96_000 };
      const rec = new MediaRecorder(streamRef.current, opts);
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const type = audioOnly ? (mime.startsWith("audio/mp4") ? "audio/mp4" : "audio/webm") : (mime.startsWith("video/mp4") ? "video/mp4" : "video/webm");
        const ext = audioOnly ? (type === "audio/mp4" ? "m4a" : "webm") : (type === "video/mp4" ? "mp4" : "webm");
        const blob = new Blob(chunksRef.current, { type });
        const file = new File([blob], `ns-${audioOnly ? "voice" : "record"}-${Date.now()}.${ext}`, { type });
        cleanup();
        setState("done");
        onDone(file, elapsedRef.current || 0);
      };
      rec.start();
      recorderRef.current = rec;
      elapsedRef.current = 0;
      setElapsed(0);
      setState("recording");
      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(elapsedRef.current);
        if (elapsedRef.current >= maxDur) {
          try { rec.stop(); } catch {}
        }
      }, 1000);
    } catch (e) {
      toast.error("Recording is not supported on this browser");
    }
  };

  const elapsedRef = useRef(0);

  const stop = () => {
    try { recorderRef.current?.stop(); } catch {}
  };

  const cleanup = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  useEffect(() => () => cleanup(), []);

  if (state === "idle" || state === "requesting") {
    return (
      <div data-testid="recorder-idle" className="rounded-2xl border-2 border-dashed border-zinc-700 hover:border-[#DC2626]/50 p-8 text-center transition-colors">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#DC2626]/15 flex items-center justify-center text-[#DC2626] mb-3">
          {audioOnly ? <Mic size={22}/> : <Video size={22}/>}
        </div>
        <p className="font-semibold text-white">{audioOnly ? "Record a voice clip" : "Record your performance"}</p>
        <p className="text-xs text-zinc-400 mt-1">Up to {maxDur}s · auto-compressed to save data</p>
        <Button data-testid="recorder-turn-on" onClick={requestCamera} disabled={state === "requesting"} className="mt-4 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold h-10 px-5">
          {state === "requesting" ? "Requesting…" : (audioOnly ? "Turn on mic" : "Turn on camera")}
        </Button>
      </div>
    );
  }

  if (audioOnly) {
    return (
      <div data-testid="recorder-live" className="rounded-2xl bg-gradient-to-br from-[#180D0D] to-[#0A0605] border border-zinc-800 p-8 relative">
        <div className="flex flex-col items-center gap-4">
          <div className={`w-24 h-24 rounded-full flex items-center justify-center ${state === "recording" ? "bg-[#DC2626] animate-pulse" : "bg-[#DC2626]/20 border-2 border-[#DC2626]/50"}`}>
            <Mic size={40} className="text-white"/>
          </div>
          {state === "recording" ? (
            <p className="text-white font-mono text-lg">{String(elapsed).padStart(2, "0")}s / {maxDur}s</p>
          ) : (
            <p className="text-zinc-300 text-sm">Ready to record</p>
          )}
          <div className="flex items-center gap-4 mt-2">
            <button data-testid="recorder-cancel" onClick={() => { cleanup(); onCancel?.(); }} className="w-10 h-10 rounded-full bg-zinc-900 border border-zinc-700 text-white flex items-center justify-center">
              <RefreshCw size={16}/>
            </button>
            {state === "previewing" ? (
              <button data-testid="recorder-start" onClick={start} className="w-16 h-16 rounded-full bg-[#DC2626] border-4 border-white flex items-center justify-center hover:bg-[#EF4444] transition-colors">
                <Circle size={22} className="text-white" fill="white"/>
              </button>
            ) : (
              <button data-testid="recorder-stop" onClick={stop} className="w-16 h-16 rounded-full bg-white border-4 border-[#DC2626] flex items-center justify-center">
                <Square size={20} className="text-[#DC2626]" fill="#DC2626"/>
              </button>
            )}
            <div className="w-10 h-10"/>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="recorder-live" className="rounded-2xl overflow-hidden bg-black relative aspect-[9/16] max-h-[70vh]">
      <video
        ref={videoRef}
        playsInline
        muted
        className="w-full h-full object-cover"
        style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }}
      />
      {state === "previewing" && (
        <button data-testid="recorder-flip" onClick={flip} className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/60 backdrop-blur border border-white/20 text-white flex items-center justify-center">
          <RotateCcw size={16}/>
        </button>
      )}
      {state === "recording" && (
        <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 backdrop-blur border border-red-500/40 rounded-full px-3 py-1">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"/>
          <span className="text-white text-sm font-mono">{String(elapsed).padStart(2, "0")}s / {maxDur}s</span>
        </div>
      )}
      <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-4">
        <button data-testid="recorder-cancel" onClick={() => { cleanup(); onCancel?.(); }} className="w-10 h-10 rounded-full bg-black/60 backdrop-blur border border-white/20 text-white flex items-center justify-center">
          <RefreshCw size={16}/>
        </button>
        {state === "previewing" ? (
          <button data-testid="recorder-start" onClick={start} className="w-16 h-16 rounded-full bg-[#DC2626] border-4 border-white flex items-center justify-center hover:bg-[#EF4444] transition-colors">
            <Circle size={22} className="text-white" fill="white"/>
          </button>
        ) : (
          <button data-testid="recorder-stop" onClick={stop} className="w-16 h-16 rounded-full bg-white border-4 border-[#DC2626] flex items-center justify-center">
            <Square size={20} className="text-[#DC2626]" fill="#DC2626"/>
          </button>
        )}
        <div className="w-10 h-10"/>
      </div>
    </div>
  );
}
